import React, { useState } from "react";
import PropTypes from "prop-types";
import { get, has, isArray } from "lodash";
import clsx from "clsx";
import Grid from "@material-ui/core/Grid/Grid";
import { Typography, useMediaQuery } from "@material-ui/core";
import BlockRoundedIcon from "@material-ui/icons/BlockRounded";
import InfoRoundedIcon from "@material-ui/icons/InfoRounded";
import RestoreRoundedIcon from "@material-ui/icons/RestoreRounded";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import withStyles from "@material-ui/core/styles/withStyles";
import Markdown from "@configuratorware/configurator-frontendgui/Framework/Components/Markdown";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import customClassName from "@configuratorware/configurator-frontendgui/App/Utils/customClassName";
import OptionAmount from "@configuratorware/configurator-frontendgui/App/Modules/Creator/Components/OptionAmount";
import getImageUrl from "@configuratorware/configurator-frontendgui/App/Utils/getImageUrl";
import { removeEmbroideryCustomData } from "../../Shared/Actions/Actions";
import { useDispatch } from "react-redux";

import { HAS_NAME_IDS, HAS_PHONE_IDS, NO_EMBROIDERY_ID } from "../../Constants";

// Custom components
import InlineCustomDataEditor from "../InlineCustomDataEditor";

const styles = (theme) => {
  const textWidthWithAmount =
    theme.creator.option.image.width !== "auto"
      ? `calc(94% - (${theme.creator.option.image.width} + 35px))`
      : theme.creator.option.textArea.textWidth;

  const customValues = {
    icon: {
      fontSize: "16px",
      lineHeight: 1.6,
    },
    image: {
      fontSize: "11px",
      lineHeight: "11px",
    },
    textArea: {
      fontSize: "0.8em",
      title: {
        fontWeight: "bold",
      },
    },
  };

  return {
    description: {},
    materialIcon: {},
    additionalsWrapper: {
      backgroundColor: "#d8d8d8",
      padding: theme.spacing(2),
      paddingTop: 0,
      "&:empty": {
        display: "none",
      },
    },

    price: {},
    details: {
      display: "inline-block",
    },
    iconWithText: {
      lineHeight: customValues.icon.lineHeight,
      display: "flex",
      verticalAlign: "middle",
      "& $materialIcon": {
        float: "left",
        display: "inline-block",
        paddingRight: "8px",
        minWidth: "20px",
        textAlign: "center",
      },
    },
    optionList: {
      overflow: "hidden",
    },
    title: {
      fontWeight: "bold",
      display: "-webkit-box",
      overflow: "hidden",
      wordWrap: "break-word",
      boxOrient: "vertical",
      lineClamp: 3,
      textOverflow: "ellipsis",
    },
    image: {
      display: "inline-block",
      float: "left",
      height: "100%",
      maxHeight: "100%",
      maxWidth: theme.creator.option.image.width,
      marginRight: "10px",
      fontSize: 0,

      // width: 48,
      // height: 48,

      "& img": {
        maxHeight: "100%",
        maxWidth: "100%",
        // width: "100%",
      },
      "& .imageNotAvailableElement": {
        padding: "4px",
        lineHeight: customValues.image.lineHeight,
        fontSize: customValues.image.fontSize,
      },
    },
    root: {
      minHeight: "100px",
      padding: 10,
      cursor: "pointer",
      fontSize: customValues.textArea.fontSize,
      width: "94%",
      "& .content": {
        position: "relative",
        verticalAlign: "middle",
        margin: 0,
      },
      "& .disabled": {
        cursor: "not-allowed",
        opacity: "0.8",
      },
      "&.selected": {
        backgroundColor: "#d8d8d8",
      },
    },
    text: {
      float: "left",
      width: "calc(94% - " + theme.creator.option.image.width + ")",
      "& a": {
        textDecoration: "underline",
      },
      "& $description": {
        marginBottom: 10,
        "& p": {
          margin: 0,
        },
      },
      "& $price": {
        marginBottom: 10,
      },
      "& $title": {
        fontWeight: customValues.textArea.title.fontWeight,
        marginBottom: "10px",
      },
    },
    withAmount: {
      "& $text": {
        width: textWidthWithAmount,
      },
    },
  };
};

const Option = ({ children, render, ...props }) => {
  const {
    className,
    onClick,
    onSelect,
    onToggle,
    option,
    optionclassification,
    selected,
    viewDetails,
    viewStockInformation,
    OptionAmountComponent,
    OptionGroupComponent,
    classes,
    isAdminMode,
  } = props;

  const [imageAvailable, setImageAvailable] = useState(true);
  const smallerScreen = useMediaQuery(
    "(min-device-width : 300px) and (max-device-width : 340px)"
  );
  const thumbnail = get(option, "thumbnail", "");
  const detailImage = get(option, "detailImage");
  const optionIdentifier = get(option, "identifier", "");
  const amountIsSelectable = get(option, "amount_is_selectable", false);
  const resultStatus = get(option, "check_results.status", true);
  const optionIsMultiSelect = get(option, "is_multiselect", false);
  const optionAmount = get(option, "amount", 0);
  const translations = {
    ...t("common"),
    ...t("optionlistitem"),
  };
  const dispatch = useDispatch();

  const setImageAvailability = () => {
    setImageAvailable(!imageAvailable);
  };

  const isEnabled = () => {
    return get(option, "stock.traffic_light", false) !== "red";
  };

  const shouldDisplayDetails = () => {
    let displayDetails = false;
    if (option.abstract || option.description || detailImage) {
      displayDetails = true;
    }

    return displayDetails;
  };

  const onOptionSelected = () => {
    const optionClassificationIsMultiselect = get(
      optionclassification,
      "is_multiselect",
      false
    );

    if (amountIsSelectable) {
      onSelect({
        ...option,
        amount: !optionAmount ? 1 : optionAmount,
      });
    } else if (optionClassificationIsMultiselect || isAdminMode) {
      onSelect({
        ...option,
        amount: selected ? 0 : 1,
      });
    } else if (!selected) {
      onSelect({
        ...option,
        amount: 1,
      });
    }
  };

  const onOptionClicked = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const isNoEmbroideryOption = NO_EMBROIDERY_ID.includes(optionIdentifier);
    if (true === isNoEmbroideryOption) {
      dispatch(removeEmbroideryCustomData());
    }


    if (isEnabled()) {
      onOptionSelected();
      onClick();
    }
  };

  const onAmountChanged = (optionAmount) => {
    if (!isEnabled()) {
      return;
    }

    let amount;

    if (amountIsSelectable) {
      amount = optionAmount;
    } else if (optionIsMultiSelect) {
      amount = selected ? 0 : 1;
    } else if (!selected) {
      amount = 1;
    }

    if (amount < 0) {
      return; // prevent setting below 0
    }
    onSelect({
      ...option,
      amount,
    });
  };

  const onDetailsClicked = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (isEnabled()) {
      viewDetails(option);
    }
  };

  const onIncompatibilityClicked = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (isEnabled()) {
      onOptionSelected();
    }
  };

  const onOptionGroupClicked = (event) => {
    event.stopPropagation();
  };

  const onOptionGroupItemSelected = (option) => {
    onSelect({
      ...option,
      amount: 1,
    });
  };

  const onLateDeliveryClicked = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (isEnabled()) {
      viewStockInformation(option);
    }
  };

  const renderImage = () => {
    const src = getImageUrl(thumbnail);
    return src && <img src={src} onError={setImageAvailability} />;
  };

  const renderNotAvailableImage = () => {
    return (
      <div className={"imageNotAvailableElement"}>
        <Typography variant={"caption"}>
          {translations.imageNotAvailable}
        </Typography>
      </div>
    );
  };

  const renderStockStatus = () => {
    if (option.stock) {
      switch (option.stock.traffic_light) {
        case "red":
          return (
            <div className={classes.iconWithText}>
              <BlockRoundedIcon className={classes.materialIcon} />{" "}
              <Typography variant={"body2"}>
                {translations.notAvailable}
              </Typography>
            </div>
          );
        case "yellow":
          return (
            <div>
              <a
                className={classes.iconWithText}
                onClick={onLateDeliveryClicked}
              >
                <RestoreRoundedIcon className={classes.materialIcon} />{" "}
                <Typography variant={"body2"}>
                  {translations.lateDelivery}
                </Typography>
              </a>
            </div>
          );
        case "green":
          return null;
      }
    }
    return null;
  };

  const renderOptionGroup = () => {
    if (!isEnabled()) {
      return null;
    }

    const optionGroup = get(option, "option_group", {});

    if (
      has(optionGroup, "children") &&
      isArray(optionGroup.children) &&
      optionGroup.children.length > 0
    ) {
      return (
        <div onClick={onOptionGroupClicked}>
          <OptionGroupComponent
            itemGroup={optionGroup}
            option={option}
            optionclassification={optionclassification}
            onOptionSelect={onOptionGroupItemSelected}
            selected={selected}
            onToggle={onToggle}
          />
        </div>
      );
    }
    return null;
  };

  const renderActions = () => {
    let displayDetails = shouldDisplayDetails();

    return (
      <div className={classes.details}>
        {displayDetails && (
          <div>
            <a className={classes.iconWithText} onClick={onDetailsClicked}>
              <InfoRoundedIcon className={classes.materialIcon} />
              <Typography variant={"body2"} className={classes.iconWithText}>
                {translations.details}
              </Typography>
            </a>
          </div>
        )}

        {resultStatus !== true && (
          <div>
            <a
              className={classes.iconWithText}
              onClick={onIncompatibilityClicked}
            >
              <WarningRoundedIcon className={classes.materialIcon} />
              <Typography variant={"body2"}>
                {translations.incompatibility}
              </Typography>
            </a>
          </div>
        )}

        {renderStockStatus()}

        {renderOptionGroup()}
      </div>
    );
  };

  const renderText = () => {
    return (
      <Grid item xs={12}>
        <Typography
          component={"div"}
          className={classes.optionList}
          variant={"body2"}
        >
          <div className={classes.title}>{option.title}</div>
          <div className={classes.price}>{option.priceFormatted}</div>
          {smallerScreen && renderControls("horizontal")}
          {option.abstract && (
            <Markdown className={classes.description}>
              {option.abstract}
            </Markdown>
          )}

          {renderActions()}
        </Typography>
      </Grid>
    );
  };

  const renderControls = (view?) => {
    if (isEnabled() && amountIsSelectable) {
      return (
        <OptionAmountComponent
          view={view}
          option={option}
          onAmountChange={onAmountChanged}
          onSelect={onSelect}
        />
      );
    } else {
      return null;
    }
  };

  const getRootClassName = () =>
    clsx(classes.root, selected && "selected", customClassName("option"));

  const getContentClassName = () => {
    const amountClass =
      amountIsSelectable && !smallerScreen ? classes.withAmount : "";
    return clsx(
      "content",
      className,
      optionIdentifier,
      amountClass,
      !isEnabled() && "disabled",
      resultStatus !== true && "incompatible"
    );
  };

  const getRenderProps = () => ({
    ...props,
    imageAvailable,
    smallerScreen,
    renderImage,
    renderNotAvailableImage,
    renderText,
    renderControls,
    renderActions,
    renderOptionGroup,
    renderStockStatus,
    setImageAvailability,
    isEnabled,
    shouldDisplayDetails,
    onOptionSelected,
    onOptionClicked,
    onAmountChanged,
    onDetailsClicked,
    onIncompatibilityClicked,
    onOptionGroupClicked,
    onOptionGroupItemSelected,
    onLateDeliveryClicked,
    getRootClassName,
    getContentClassName,
  });

  const renderItem = () => {
    const hasName =
      HAS_NAME_IDS.includes(props.option.identifier) && props.selected;
    const hasPhone =
      HAS_PHONE_IDS.includes(props.option.identifier) && props.selected;

    return (
      <>
        <div
          className={getRootClassName()}
          data-option={option.identifier}
          onClick={onOptionClicked}
        >
          <div className={getContentClassName()}>
            {typeof children === "function" ? (
              children(getRenderProps())
            ) : (
              <>
                <div className={classes.image}>
                  {imageAvailable ? renderImage() : renderNotAvailableImage()}
                </div>

                <div className={classes.text}>{renderText()}</div>

                {!smallerScreen && renderControls()}
              </>
            )}
          </div>
        </div>
        <div className={classes.additionalsWrapper}>
          {(hasName || hasPhone) && (
            <InlineCustomDataEditor hasName={hasName} hasPhone={hasPhone} />
          )}
        </div>
      </>
    );
  };

  return typeof render === "function" ? render(getRenderProps()) : renderItem();
};

Option.defaultProps = {
  className: "",
  onClick: () => {},
  onSelect: () => {},
  onToggle: () => {},
  optionclassification: {},
  option: {},
  selected: false,
  translations: {},
  viewDetails: () => {},
  viewStockInformation: () => {},
  OptionAmountComponent: OptionAmount,
  OptionGroupComponent: React.Fragment,
};

Option.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  onSelect: PropTypes.func,
  onToggle: PropTypes.func,
  option: PropTypes.object,
  optionclassification: PropTypes.object,
  selected: PropTypes.bool,
  translations: PropTypes.object,
  viewDetails: PropTypes.func,
  viewStockInformation: PropTypes.func,
  OptionAmountComponent: PropTypes.elementType,
  OptionGroupComponent: PropTypes.elementType,
  isAdminMode: PropTypes.bool,
  children: PropTypes.func,
  render: PropTypes.func,
};

// @ts-ignore
export default withStyles(styles, { name: "Option" })(Option);
